import { faChevronDown, faChevronUp, faFilter } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Box, Button, Typography } from "@mui/material"
import { getAdvertiserNameById, getAllAdvertiserIds } from "advertiser/tools/common"
import { translate } from "app/language/service"
import {
  FILTER_BODY_HEIGHT_IN_REM,
  FILTER_EXPANDED_HEIGHT_IN_REM,
  FILTER_HEADER_HEIGHT_IN_REM,
  ICampaignFilters,
  getStatusFilters
} from "campaign/model"
import { setCampaignFilterPanelExpansion } from "campaign/tools/common"
import { clearListFilters, getActiveFilterCount, setCampaignFilters } from "campaign/tools/filters"
import { getAllOrderIds, getOrderNameById } from "order/tools/common"
import React from "react"
import { FlexBox } from "ui/components/FlexBox"
import { TimePeriod } from "ui/components/TimePeriod"
import { getCreationDateFilters } from "ui/model"
import { TimeFilter } from "../../../ui/components/TimeFilter"
import { AutoCompleteSelect } from "./AutoCompleteSelect"
import { StatusFilter } from "./StatusFilter"
import { TextFilter } from "./TextFilter"

interface IFiltersProps {
  isExpanded: boolean
  filters: ICampaignFilters
  isForecast: boolean
  isDspDeal: boolean
  isPhone: boolean
}

export const Filters = React.memo(
  ({ isExpanded, filters, isForecast, isDspDeal, isPhone }: IFiltersProps): JSX.Element => {
    const statusFilters = getStatusFilters(isDspDeal)
    const activeFilterCount = getActiveFilterCount(filters)
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: isExpanded ? `${FILTER_EXPANDED_HEIGHT_IN_REM}rem` : `${FILTER_HEADER_HEIGHT_IN_REM}rem`,
          border: theme => theme.palette.border,
          width: "100%",
          mt: "1rem",
          overflow: "hidden",
          borderRadius: ".25rem",
          transition: "all 400ms ease"
        }}>
        <Box
          id="filter-header"
          sx={{
            "width": "100%",
            "height": `${FILTER_HEADER_HEIGHT_IN_REM}rem`,
            "display": "flex",
            "alignItems": "center",
            "justifyContent": "space-between",
            "cursor": "pointer",
            "backgroundColor": theme => theme.palette.background.paper,
            "p": ".5rem",
            "pl": "1rem",
            "pr": "1rem",
            "borderTopLeftRadius": ".25rem",
            "borderTopRightRadius": ".25rem",
            "& .campaign-filter-icon": {
              color: theme => theme.palette.text.primary,
              mr: ".5rem"
            },
            "& .campaign-expansion-icon": {
              color: theme => theme.palette.text.primary,
              mt: ".15rem"
            }
          }}
          onClick={() => setCampaignFilterPanelExpansion(!isExpanded, isForecast, isDspDeal)}>
          <Typography
            sx={{
              "fontSize": "1.1rem",
              "color": theme => theme.palette.text.primary,
              "& .campaign-filter-icon": {
                color: theme =>
                  `${activeFilterCount > 0 ? theme.palette.primary.main : theme.palette.text.primary} !important`
              }
            }}>
            <FontAwesomeIcon icon={faFilter} className="campaign-filter-icon" />
            {translate("label.filters")}
            {!isPhone && (
              <>
                {" "}
                <Typography id="filter-active-count" component="span" sx={{ fontSize: ".9rem" }} color="textSecondary">
                  {`- ${translate("active.count", activeFilterCount)}`}
                </Typography>
              </>
            )}
          </Typography>
          <FlexBox>
            <Button
              id="filter-clear-button"
              disabled={activeFilterCount === 0}
              variant="text"
              onClick={event => {
                event.stopPropagation()
                clearListFilters(isForecast, isDspDeal)
              }}
              sx={{ height: "1.8rem", mr: "1rem" }}>
              {translate("label.clear.filters")}
            </Button>
            <FontAwesomeIcon icon={isExpanded ? faChevronUp : faChevronDown} className="campaign-expansion-icon" />
          </FlexBox>
        </Box>
        <Box
          id="filter-content"
          sx={{
            "width": "100%",
            "height": isExpanded ? `${FILTER_BODY_HEIGHT_IN_REM}rem` : 0,
            "minHeight": isExpanded ? `${FILTER_BODY_HEIGHT_IN_REM}rem` : 0,
            "maxHeight": isExpanded ? `${FILTER_BODY_HEIGHT_IN_REM}rem` : 0,
            "p": ".5rem",
            "pl": "1rem",
            "pr": "1rem",
            "pt": 0,
            "transition": "all 400ms ease",
            "overflow": "auto",
            "display": "flex",
            "alignItems": "start",
            "justifyContent": "space-around",
            "flexWrap": "wrap",
            "& > *": {
              mt: "1rem !important"
            },
            "& .MuiAutocomplete-tag": {
              maxWidth: "12rem !important"
            }
          }}>
          <TimeFilter
            id="campaign-filter-creation-date"
            filters={getCreationDateFilters()}
            value={filters.creationDate}
            label={translate("label.creationDate")}
            onChange={creationDate => setCampaignFilters({ creationDate }, isForecast, isDspDeal)}
            sx={{ minWidth: "14rem", maxWidth: "14rem" }}
          />
          {!isForecast && (
            <>
              <StatusFilter
                id="campaign-filter-status"
                filters={statusFilters}
                value={filters.statuses}
                label={translate("label.status")}
                onChange={statuses => setCampaignFilters({ statuses }, isForecast, isDspDeal)}
                sx={{ minWidth: "14rem" }}
              />
              {!isDspDeal && (
                <AutoCompleteSelect
                  id="campaign-filter-advertiser"
                  filters={getAllAdvertiserIds()}
                  value={filters.advertiserIds}
                  label={translate("label.advertiser")}
                  optionLabelFn={(advertiserId: number) => getAdvertiserNameById(advertiserId)}
                  onChange={advertiserIds =>
                    setCampaignFilters({ advertiserIds: advertiserIds as number[] }, isForecast, isDspDeal)
                  }
                  sx={{ minWidth: "14rem" }}
                />
              )}
            </>
          )}
          <TextFilter
            id="campaign-filter-name"
            value={filters.name}
            label={translate(
              isForecast ? "forecast.name.search" : isDspDeal ? "dsp-deal.name.search" : "campaign.name.search"
            )}
            onChange={newValue => setCampaignFilters({ name: newValue }, isForecast, isDspDeal)}
            sx={{ minWidth: "14rem" }}
          />
          {!isForecast && (
            <AutoCompleteSelect
              id="campaign-filter-order"
              filters={getAllOrderIds()}
              value={filters.orderIds}
              label={translate("label.order")}
              optionLabelFn={(orderId: number) => getOrderNameById(orderId)}
              onChange={orderIds => setCampaignFilters({ orderIds: orderIds as number[] }, isForecast, isDspDeal)}
              sx={{ minWidth: "14rem" }}
            />
          )}
          <TimePeriod
            id="campaign-filter-start-end"
            value={filters.startEndPeriod}
            label={translate("label.start.end")}
            onChange={startEndPeriod => setCampaignFilters({ startEndPeriod }, isForecast, isDspDeal)}
            sx={{ minWidth: "14rem" }}
          />
        </Box>
      </Box>
    )
  }
)
